import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Image as ImageIcon, Lock, Globe, Loader2, Sparkles, RefreshCw, ArrowRight, Mic } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useLanguage } from '@/i18n/LanguageContext';
import { getAuthHeaders } from '@/lib/authHeaders';
import { useAnalytics } from '@/hooks/useAnalytics';
import { VoiceInput } from '@/components/voice/VoiceInput';
import { toast } from 'sonner';
import type { TreslogEntry, TreslogEntryInsert, TreslogEntryUpdate } from '@/types/treslog';

interface TreslogEntryEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry?: TreslogEntry | null;
  onSave: (data: TreslogEntryInsert | TreslogEntryUpdate) => Promise<void>;
  onUploadPhoto: (file: File) => Promise<string | null>;
}

type MoodType = 'reflective' | 'grateful' | 'anxious' | 'peaceful' | 'hopeful' | 'sad' | 'overwhelmed' | 'proud' | 'uncertain' | null;

interface MoodOption {
  value: MoodType;
  emoji: string;
  label: { en: string; es: string };
}

const MOOD_OPTIONS: MoodOption[] = [
  { value: 'reflective', emoji: '🪞', label: { en: 'Reflective', es: 'Reflexivo' } },
  { value: 'grateful', emoji: '🙏', label: { en: 'Grateful', es: 'Agradecido' } },
  { value: 'peaceful', emoji: '☁️', label: { en: 'Peaceful', es: 'En paz' } },
  { value: 'hopeful', emoji: '🌱', label: { en: 'Hopeful', es: 'Esperanzado' } },
  { value: 'proud', emoji: '✨', label: { en: 'Proud', es: 'Orgulloso' } },
  { value: 'uncertain', emoji: '🌫️', label: { en: 'Uncertain', es: 'Incierto' } },
  { value: 'anxious', emoji: '🌀', label: { en: 'Anxious', es: 'Ansioso' } },
  { value: 'overwhelmed', emoji: '🌊', label: { en: 'Overwhelmed', es: 'Abrumado' } },
  { value: 'sad', emoji: '🌧️', label: { en: 'Sad', es: 'Triste' } },
];

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export function TreslogEntryEditor({
  open,
  onOpenChange,
  entry,
  onSave,
  onUploadPhoto,
}: TreslogEntryEditorProps) {
  const { language } = useLanguage();
  const { trackTreslogAiAssistUsed } = useAnalytics();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [title, setTitle] = useState(entry?.title || '');
  const [content, setContent] = useState(entry?.content || '');
  const [photoUrls, setPhotoUrls] = useState<string[]>(entry?.photo_urls || []);
  const [isPublic, setIsPublic] = useState(entry?.is_public || false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Mood & AI prompts state
  const [selectedMood, setSelectedMood] = useState<MoodType>(null);
  const [aiPrompts, setAiPrompts] = useState<string[]>([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);

  const isEditing = !!entry;

  const translations = {
    en: {
      createTitle: 'New TRESLOG Entry',
      editTitle: 'Edit Entry',
      titleLabel: 'Title',
      titlePlaceholder: 'Give this entry a title...',
      contentLabel: 'Your Story',
      contentPlaceholder: 'Write about yourself in the third person. Step back and see your story with clarity...\n\nExample: "She sat by the window, watching the rain. It had been a long week, but she found peace in small moments like this..."',
      addPhotos: 'Add Photos',
      uploading: 'Uploading...',
      privacyLabel: 'Share publicly',
      privacyDescription: 'When enabled, this entry can be viewed by anyone with the link',
      privateNote: 'This entry is private. Only you can see it.',
      cancel: 'Cancel',
      save: 'Save Entry',
      saving: 'Saving...',
      update: 'Update Entry',
      photoLimit: 'You can add up to 5 photos',
      moodLabel: 'How are you feeling?',
      moodHint: 'Select a mood to get personalized prompts',
      aiHelp: 'Need inspiration?',
      aiHelpDesc: 'Let AI suggest gentle third-person prompts',
      getPrompts: 'Get writing prompts',
      refreshPrompts: 'Try different prompts',
      continueWriting: 'Continue my writing',
      rephraseToThird: 'Rephrase in third person',
      usePrompt: 'Use this',
      loadingPrompts: 'Finding inspiration...',
      promptsTitle: 'Writing inspiration',
    },
    es: {
      createTitle: 'Nueva Entrada de TRESLOG',
      editTitle: 'Editar Entrada',
      titleLabel: 'Título',
      titlePlaceholder: 'Dale un título a esta entrada...',
      contentLabel: 'Tu Historia',
      contentPlaceholder: 'Escribe sobre ti en tercera persona. Da un paso atrás y ve tu historia con claridad...\n\nEjemplo: "Ella se sentó junto a la ventana, mirando la lluvia. Había sido una semana larga, pero encontraba paz en pequeños momentos como este..."',
      addPhotos: 'Agregar Fotos',
      uploading: 'Subiendo...',
      privacyLabel: 'Compartir públicamente',
      privacyDescription: 'Cuando está habilitado, esta entrada puede ser vista por cualquiera con el enlace',
      privateNote: 'Esta entrada es privada. Solo tú puedes verla.',
      cancel: 'Cancelar',
      save: 'Guardar Entrada',
      saving: 'Guardando...',
      update: 'Actualizar Entrada',
      photoLimit: 'Puedes agregar hasta 5 fotos',
      moodLabel: '¿Cómo te sientes?',
      moodHint: 'Selecciona un estado de ánimo para obtener sugerencias personalizadas',
      aiHelp: '¿Necesitas inspiración?',
      aiHelpDesc: 'Deja que la IA sugiera prompts en tercera persona',
      getPrompts: 'Obtener sugerencias',
      refreshPrompts: 'Probar otras sugerencias',
      continueWriting: 'Continuar mi escritura',
      rephraseToThird: 'Reformular en tercera persona',
      usePrompt: 'Usar esto',
      loadingPrompts: 'Buscando inspiración...',
      promptsTitle: 'Inspiración para escribir',
    },
  };

  const t = translations[language];

  const fetchAIPrompts = useCallback(async (type: 'starter' | 'continue' | 'rephrase') => {
    setIsLoadingPrompts(true);
    setShowPrompts(true);
    
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/treslog-ai-prompts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          type,
          currentContent: content,
          title,
          mood: selectedMood,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          toast.error('Please wait a moment before trying again');
          return;
        }
        throw new Error('Failed to get prompts');
      }

      const data = await response.json();
      setAiPrompts(data.prompts || []);
      trackTreslogAiAssistUsed(type);
    } catch (error) {
      console.error('AI prompts error:', error);
      toast.error('Could not generate prompts. Please try again.');
      setShowPrompts(false);
    } finally {
      setIsLoadingPrompts(false);
    }
  }, [content, title, selectedMood, trackTreslogAiAssistUsed]);

  const handleUsePrompt = (prompt: string) => {
    if (content.trim()) {
      setContent(prev => prev + '\n\n' + prompt);
    } else {
      setContent(prompt);
    }
    setShowPrompts(false);
    setAiPrompts([]);
  };

  const handleMoodSelect = (mood: MoodType) => {
    setSelectedMood(selectedMood === mood ? null : mood);
    // Clear prompts when mood changes
    setAiPrompts([]);
    setShowPrompts(false);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    if (photoUrls.length + files.length > 5) {
      toast.error(t.photoLimit);
      return;
    }

    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error('File size must be under 5MB');
          continue;
        }
        const url = await onUploadPhoto(file);
        if (url) {
          setPhotoUrls((prev) => [...prev, url]);
        }
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotoUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        title: title.trim(),
        content: content.trim(),
        photo_urls: photoUrls,
        is_public: isPublic,
      });
      onOpenChange(false);
      toast.success(isEditing ? 'Entry updated' : 'Entry saved');
    } catch (error) {
      toast.error('Failed to save entry');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form when closing
      setTitle(entry?.title || '');
      setContent(entry?.content || '');
      setPhotoUrls(entry?.photo_urls || []);
      setIsPublic(entry?.is_public || false);
      setSelectedMood(null);
      setAiPrompts([]);
      setShowPrompts(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t.editTitle : t.createTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">{t.titleLabel}</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.titlePlaceholder}
            />
          </div>

          {/* Mood Selector */}
          <div className="space-y-3">
            <div>
              <Label>{t.moodLabel}</Label>
              <p className="text-xs text-muted-foreground mt-0.5">{t.moodHint}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {MOOD_OPTIONS.map((mood) => (
                <button
                  key={mood.value}
                  type="button"
                  onClick={() => handleMoodSelect(mood.value)}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all
                    ${selectedMood === mood.value
                      ? 'bg-primary text-primary-foreground shadow-sm scale-105'
                      : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <span>{mood.emoji}</span>
                  <span>{mood.label[language]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* AI Writing Prompts */}
          <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{t.aiHelp}</span>
              </div>
              {selectedMood && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  {MOOD_OPTIONS.find(m => m.value === selectedMood)?.emoji} {MOOD_OPTIONS.find(m => m.value === selectedMood)?.label[language]}
                </span>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground">{t.aiHelpDesc}</p>
            
            <div className="flex flex-wrap gap-2">
              {!content.trim() ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fetchAIPrompts('starter')}
                  disabled={isLoadingPrompts}
                  className="gap-2"
                >
                  {isLoadingPrompts ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  {t.getPrompts}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fetchAIPrompts('continue')}
                    disabled={isLoadingPrompts}
                    className="gap-2"
                  >
                    {isLoadingPrompts ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <ArrowRight className="h-3 w-3" />
                    )}
                    {t.continueWriting}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fetchAIPrompts('rephrase')}
                    disabled={isLoadingPrompts}
                    className="gap-2"
                  >
                    <RefreshCw className="h-3 w-3" />
                    {t.rephraseToThird}
                  </Button>
                </>
              )}
            </div>

            {/* AI Prompts Results */}
            <AnimatePresence>
              {showPrompts && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-2 pt-2"
                >
                  {isLoadingPrompts ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t.loadingPrompts}
                    </div>
                  ) : (
                    <>
                      <p className="text-xs font-medium text-muted-foreground">{t.promptsTitle}</p>
                      <div className="space-y-2">
                        {aiPrompts.map((prompt, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="bg-background rounded-md p-3 border border-border/50"
                          >
                            <p className="text-sm text-foreground/90 italic leading-relaxed">
                              "{prompt}"
                            </p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUsePrompt(prompt)}
                              className="mt-2 h-7 text-xs text-primary hover:text-primary"
                            >
                              {t.usePrompt}
                            </Button>
                          </motion.div>
                        ))}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchAIPrompts(content.trim() ? 'continue' : 'starter')}
                        className="gap-2 text-xs"
                      >
                        <RefreshCw className="h-3 w-3" />
                        {t.refreshPrompts}
                      </Button>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Content */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="content">{t.contentLabel}</Label>
              <VoiceInput 
                onTranscript={(text) => setContent(prev => prev ? prev + ' ' + text : text)}
                size="icon"
              />
            </div>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t.contentPlaceholder}
              className="min-h-[200px] resize-none"
            />
          </div>

          {/* Photos */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>{t.addPhotos}</Label>
              <span className="text-xs text-muted-foreground">{photoUrls.length}/5</span>
            </div>
            
            <AnimatePresence>
              {photoUrls.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex gap-2 flex-wrap"
                >
                  {photoUrls.map((url, idx) => (
                    <motion.div
                      key={url}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="relative group"
                    >
                      <img
                        src={url}
                        alt=""
                        className="h-24 w-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(idx)}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || photoUrls.length >= 5}
              className="gap-2"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t.uploading}
                </>
              ) : (
                <>
                  <ImageIcon className="h-4 w-4" />
                  {t.addPhotos}
                </>
              )}
            </Button>
          </div>

          {/* Privacy Toggle */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="privacy" className="flex items-center gap-2">
                  {isPublic ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                  {t.privacyLabel}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t.privacyDescription}
                </p>
              </div>
              <Switch
                id="privacy"
                checked={isPublic}
                onCheckedChange={setIsPublic}
              />
            </div>
            {!isPublic && (
              <p className="text-xs text-muted-foreground/80 flex items-center gap-1">
                <Lock className="h-3 w-3" />
                {t.privateNote}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t.cancel}
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !title.trim() || !content.trim()}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t.saving}
              </>
            ) : (
              isEditing ? t.update : t.save
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
